import { useState, useCallback, useMemo } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { colors } from '@/core/styles/tokens'
import { useAdminPayrollSummary, useEarliestPayPeriod } from '@/common/hooks/useAdminTimesheetSummary'
import type { EmployeeTimesheetStatus } from '@/common/hooks/useAdminTimesheetSummary'
import { downloadFile } from '@/common/lib/fetchApi'
import { AdminOverrideBadge } from '@/common/components/admin-override-badge/AdminOverrideBadge'
import { ReopenTimesheetDialog } from '@/common/components/reopen-timesheet-dialog'
import * as tsStyles from '@/common/components/timesheet-page/TimesheetPage.css'
import * as styles from './AdminPayrollTable.css'

function formatPeriodLabel(start: string, end: string): string {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' }
  if (s.getFullYear() === e.getFullYear()) {
    return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', yearOpts)}`
  }
  return `${s.toLocaleDateString('en-US', yearOpts)} – ${e.toLocaleDateString('en-US', yearOpts)}`
}

function formatShortDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function getStateBadgeClass(state: string): string {
  switch (state) {
    case 'active': return styles.stateActive
    case 'drafting': return styles.stateDrafting
    case 'closed': return styles.stateClosed
    default: return styles.stateBadge
  }
}

function getRowClass(status: string): string {
  switch (status) {
    case 'missing': return styles.rowMissing
    case 'draft': return styles.rowDraft
    case 'rejected': return styles.rowRejected
    case 'approved': return styles.rowApproved
    default: return ''
  }
}

type SortKey = 'employee' | 'manager' | 'division' | 'submitted' | 'approved' | 'total'

function sortValue(emp: EmployeeTimesheetStatus, key: SortKey): string | number {
  switch (key) {
    case 'employee': return emp.employee_name.toLowerCase()
    case 'manager': return (emp.manager_name ?? '').toLowerCase()
    case 'division': return (emp.department ?? '').toLowerCase()
    case 'submitted': return emp.submitted_at ?? ''
    case 'approved': return emp.approved_at ?? ''
    case 'total': return emp.total_hours
  }
}

const STATUS_FILTERS = ['all', 'submitted', 'approved', 'draft', 'rejected', 'missing'] as const

/** Only filed timesheets can be sent back to the employee for correction. */
function isReopenable(emp: EmployeeTimesheetStatus): boolean {
  return emp.timesheet_id != null && (emp.status === 'submitted' || emp.status === 'approved')
}

export function AdminPayrollTable() {
  const navigate = useNavigate()
  const [periodStart, setPeriodStart] = useState<string | undefined>(undefined)
  const [copiedCell, setCopiedCell] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [managerFilter, setManagerFilter] = useState<string>('all')
  const [divisionFilter, setDivisionFilter] = useState<string>('all')
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const [reopenTarget, setReopenTarget] = useState<EmployeeTimesheetStatus | null>(null)

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const handleOpenRow = useCallback(
    (timesheetId: number | null) => {
      if (timesheetId != null) {
        navigate({ to: '/manager/review/$timesheetId', params: { timesheetId: String(timesheetId) } })
      }
    },
    [navigate],
  )

  const { data, isLoading, error } = useAdminPayrollSummary(periodStart)
  const { data: earliestPeriod } = useEarliestPayPeriod()

  const isFirstPeriod = earliestPeriod && data
    ? data.pay_period.start_date <= earliestPeriod.start_date
    : false

  const handlePrev = () => {
    if (data) setPeriodStart(addDays(data.pay_period.start_date, -1))
  }
  const handleNext = () => {
    if (data) setPeriodStart(addDays(data.pay_period.end_date, 1))
  }

  const handleExport = useCallback(
    async (format: 'xlsx' | 'pdf') => {
      if (!data) return
      setExportError(null)
      setExporting(format)
      try {
        const periodId = data.pay_period.id
        await downloadFile(
          `/api/org/timesheets/export.${format}?pay_period_id=${periodId}`,
          `payroll-overview.${format}`,
        )
      } catch (e) {
        setExportError(e instanceof Error ? e.message : 'Export failed')
      } finally {
        setExporting(null)
      }
    },
    [data],
  )

  const handleCopyHours = useCallback(async (value: number, cellKey: string) => {
    if (value === 0) return
    try {
      await navigator.clipboard.writeText(value.toFixed(2))
      setCopiedCell(cellKey)
      setTimeout(() => setCopiedCell(null), 800)
    } catch {
      // clipboard API may fail in some contexts
    }
  }, [])

  const employees = useMemo(() => data?.employees ?? [], [data])

  const managerOptions = useMemo(
    () => [...new Set(employees.map((e) => e.manager_name).filter((m): m is string => !!m))].sort(),
    [employees],
  )
  const divisionOptions = useMemo(
    () => [...new Set(employees.map((e) => e.department).filter((d): d is string => !!d))].sort(),
    [employees],
  )

  const visibleEmployees = useMemo(() => {
    let rows = employees.filter((e) => {
      if (statusFilter !== 'all' && e.status !== statusFilter) return false
      if (managerFilter !== 'all' && (e.manager_name ?? '') !== managerFilter) return false
      if (divisionFilter !== 'all' && (e.department ?? '') !== divisionFilter) return false
      return true
    })
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1
      rows = [...rows].sort((a, b) => {
        const av = sortValue(a, sortKey)
        const bv = sortValue(b, sortKey)
        if (av < bv) return -1 * dir
        if (av > bv) return 1 * dir
        return 0
      })
    }
    return rows
  }, [employees, statusFilter, managerFilter, divisionFilter, sortKey, sortDir])

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : ''

  if (isLoading) {
    return <div className={tsStyles.page}>Loading...</div>
  }

  if (error) {
    return <div className={tsStyles.page}>Error: {error.message}</div>
  }

  if (!data) {
    return <div className={tsStyles.page}>No data available.</div>
  }

  const { pay_period, summary } = data

  return (
    <div className={styles.container}>
      <div className={tsStyles.header}>
        <h1 className={tsStyles.title}>Payroll overview</h1>
        <span className={getStateBadgeClass(pay_period.state)}>
          {pay_period.state}
        </span>
      </div>

      {/* Period navigation */}
      <div className={tsStyles.periodNav}>
        <button className={tsStyles.navButton} onClick={handlePrev} disabled={isFirstPeriod}>
          &larr; Previous
        </button>
        <span className={tsStyles.periodLabel}>
          {formatPeriodLabel(pay_period.start_date, pay_period.end_date)}
        </span>
        <button className={tsStyles.navButton} onClick={handleNext}>
          Next &rarr;
        </button>
      </div>

      {/* Summary counts */}
      <div className={styles.summaryBar}>
        <span className={styles.summaryItem}><strong>{summary.total}</strong> employees</span>
        <span className={styles.summaryItem}>{summary.approved} approved</span>
        <span className={styles.summaryItem}>{summary.submitted} submitted</span>
        <span className={styles.summaryItem}>{summary.draft} draft</span>
        <span className={styles.summaryItem}>{summary.missing} missing</span>
        {summary.rejected > 0 && (
          <span className={styles.summaryItem}>{summary.rejected} rejected</span>
        )}
      </div>

      {/* Filters */}
      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Status</span>
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Reporting to</span>
          <select
            className={styles.filterSelect}
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
          >
            <option value="all">All</option>
            {managerOptions.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Division</span>
          <select
            className={styles.filterSelect}
            value={divisionFilter}
            onChange={(e) => setDivisionFilter(e.target.value)}
          >
            <option value="all">All</option>
            {divisionOptions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div className={styles.exportGroup}>
          <span className={styles.filterLabel}>Export employees</span>
          <button
            className={styles.exportButton}
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null}
            title="Download employees as an Excel spreadsheet"
          >
            {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
          </button>
          <button
            className={styles.exportButton}
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null}
            title="Download employees as a PDF"
          >
            {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </button>
        </div>
      </div>

      {exportError && <div className={styles.exportError}>{exportError}</div>}

      {/* Payroll table */}
      <div className={tsStyles.gridScroll}>
        <table className={tsStyles.grid}>
          <thead>
            <tr>
              <th className={tsStyles.gridHeaderCell} style={{ textAlign: 'left' }}>#</th>
              <th
                className={`${tsStyles.gridHeaderCell} ${styles.sortableHeader}`}
                style={{ textAlign: 'left' }}
                onClick={() => handleSort('employee')}
              >
                Employee<span className={styles.sortIndicator}>{sortArrow('employee')}</span>
              </th>
              <th className={tsStyles.gridHeaderCell} style={{ textAlign: 'left' }}>Email</th>
              <th
                className={`${tsStyles.gridHeaderCell} ${styles.sortableHeader}`}
                style={{ textAlign: 'left' }}
                onClick={() => handleSort('manager')}
              >
                Reporting To<span className={styles.sortIndicator}>{sortArrow('manager')}</span>
              </th>
              <th
                className={`${tsStyles.gridHeaderCell} ${styles.sortableHeader}`}
                style={{ textAlign: 'left' }}
                onClick={() => handleSort('division')}
              >
                Division<span className={styles.sortIndicator}>{sortArrow('division')}</span>
              </th>
              <th className={tsStyles.gridHeaderCell} style={{ textAlign: 'left' }}>Company</th>
              <th
                className={`${tsStyles.gridHeaderCell} ${styles.sortableHeader}`}
                onClick={() => handleSort('submitted')}
              >
                Submitted<span className={styles.sortIndicator}>{sortArrow('submitted')}</span>
              </th>
              <th
                className={`${tsStyles.gridHeaderCell} ${styles.sortableHeader}`}
                onClick={() => handleSort('approved')}
              >
                Approved<span className={styles.sortIndicator}>{sortArrow('approved')}</span>
              </th>
              <th className={tsStyles.gridHeaderCell}>Std Hours</th>
              <th className={tsStyles.gridHeaderCell}>Straight OT (1.0x)</th>
              <th className={tsStyles.gridHeaderCell}>OT (1.5x)</th>
              <th
                className={`${tsStyles.gridHeaderCell} ${styles.sortableHeader}`}
                onClick={() => handleSort('total')}
              >
                Total<span className={styles.sortIndicator}>{sortArrow('total')}</span>
              </th>
              <th className={tsStyles.gridHeaderCell} style={{ textAlign: 'left' }}>Notes</th>
              <th className={tsStyles.gridHeaderCell} style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleEmployees.length === 0 ? (
              <tr>
                <td className={styles.emptyRow} colSpan={14}>
                  No employees match the current filters.
                </td>
              </tr>
            ) : (
              visibleEmployees.map((emp) => (
                <EmployeeRow
                  key={emp.employee_id}
                  emp={emp}
                  copiedCell={copiedCell}
                  onCopyHours={handleCopyHours}
                  onOpen={handleOpenRow}
                  onReopen={setReopenTarget}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <ReopenTimesheetDialog
        timesheetId={reopenTarget?.timesheet_id ?? null}
        employeeName={reopenTarget?.employee_name}
        payPeriodLabel={formatPeriodLabel(pay_period.start_date, pay_period.end_date)}
        onClose={() => setReopenTarget(null)}
      />
    </div>
  )
}

function EmployeeRow({
  emp,
  copiedCell,
  onCopyHours,
  onOpen,
  onReopen,
}: {
  emp: EmployeeTimesheetStatus
  copiedCell: string | null
  onCopyHours: (value: number, cellKey: string) => void
  onOpen: (timesheetId: number | null) => void
  onReopen: (emp: EmployeeTimesheetStatus) => void
}) {
  const clickable = emp.timesheet_id != null
  const cellClass = (field: string) => {
    const key = `${emp.employee_id}-${field}`
    return `${styles.hoursCell} ${copiedCell === key ? styles.hoursCopied : ''}`
  }

  const isCopied = (field: string) => copiedCell === `${emp.employee_id}-${field}`

  const renderHoursCell = (value: number, field: string, bold?: boolean) => (
    <td
      className={cellClass(field)}
      onClick={(e) => {
        e.stopPropagation()
        onCopyHours(value, `${emp.employee_id}-${field}`)
      }}
      title={value > 0 ? 'Click to copy' : undefined}
    >
      {value > 0 ? (
        <>
          {bold ? <strong>{value.toFixed(2)}</strong> : value.toFixed(2)}
          <span className={isCopied(field) ? styles.copiedIcon : styles.copyIcon}>
            {isCopied(field) ? '✓' : '📋'}
          </span>
        </>
      ) : ''}
    </td>
  )

  return (
    <tr
      className={`${tsStyles.dayRow} ${getRowClass(emp.status)} ${clickable ? styles.clickableRow : ''}`}
      onClick={clickable ? () => onOpen(emp.timesheet_id) : undefined}
      role={clickable ? 'link' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpen(emp.timesheet_id)
              }
            }
          : undefined
      }
      title={clickable ? 'Open timesheet for review' : undefined}
    >
      <td className={tsStyles.dayLabel} style={{ minWidth: 'auto' }}>{emp.employee_id}</td>
      <td className={tsStyles.dayLabel}>
        <Link
          to="/org/users/$employeeId"
          params={{ employeeId: String(emp.employee_id) }}
          onClick={(e) => e.stopPropagation()}
          style={{ color: colors.sea, fontWeight: 500 }}
          title="View user profile"
        >
          {emp.employee_name}
        </Link>
      </td>
      <td className={tsStyles.computedCell} style={{ textAlign: 'left' }}>{emp.work_email ?? ''}</td>
      <td className={tsStyles.computedCell} style={{ textAlign: 'left' }}>{emp.manager_name ?? ''}</td>
      <td className={tsStyles.computedCell} style={{ textAlign: 'left' }}>{emp.department ?? ''}</td>
      <td className={tsStyles.computedCell} style={{ textAlign: 'left' }}>{emp.company ?? ''}</td>
      <td className={tsStyles.computedCell}>
        {emp.submitted_at ? (
          <span className={styles.dateValue}>{formatShortDate(emp.submitted_at)}</span>
        ) : emp.status === 'submitted' || emp.status === 'approved' ? (
          <span className={styles.checkIcon}>✓</span>
        ) : (
          <span className={styles.warningIcon} title="Not submitted">⚠</span>
        )}
      </td>
      <td className={tsStyles.computedCell}>
        {emp.approved_at ? (
          <span className={styles.dateValue}>{formatShortDate(emp.approved_at)}</span>
        ) : emp.status === 'approved' ? (
          <span className={styles.checkIcon}>✓</span>
        ) : emp.status === 'submitted' ? (
          <span className={styles.pendingIcon} title="Pending approval">⏳</span>
        ) : (
          <span className={styles.warningIcon} title="Not approved">⚠</span>
        )}
      </td>
      {renderHoursCell(emp.standard_hours, 'std')}
      {renderHoursCell(emp.straight_overtime_hours, 'sot')}
      {renderHoursCell(emp.flsa_overtime_hours, 'fot')}
      {renderHoursCell(emp.total_hours, 'total', true)}
      <td className={tsStyles.computedCell} style={{ textAlign: 'left' }}>
        {emp.status === 'rejected' ? (
          <span className={styles.rejectedNote} title="Timesheet rejected">Rejected</span>
        ) : emp.is_admin_override ? (
          <AdminOverrideBadge compact />
        ) : null}
      </td>
      <td className={tsStyles.computedCell} style={{ textAlign: 'left' }}>
        {isReopenable(emp) && (
          <button
            className={styles.reopenButton}
            onClick={(e) => {
              e.stopPropagation()
              onReopen(emp)
            }}
            title="Send this timesheet back to the employee for correction"
          >
            Reopen
          </button>
        )}
      </td>
    </tr>
  )
}
